import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth, defaultRoute } from "@/lib/auth";
import { Toaster } from "sonner";
import "@/App.css";
import Login from "@/pages/Login";
import Setup from "@/pages/Setup";
import AppLayout from "@/components/AppLayout";
import MyDashboard from "@/pages/MyDashboard";
import WhatsNew from "@/pages/WhatsNew";
import Backups from "@/pages/Backups";
import Companies from "@/pages/Companies";
import Contacts from "@/pages/Contacts";
import Suppliers from "@/pages/Suppliers";
import Materials from "@/pages/Materials";
import MaterialOrders from "@/pages/MaterialOrders";
import Deals from "@/pages/Deals";
import Activities from "@/pages/Activities";
import Quotes from "@/pages/Quotes";
import QuoteBuilder from "@/pages/QuoteBuilder";
import QuoteBuilderV2 from "@/pages/QuoteBuilderV2";
import QuotePrint from "@/pages/QuotePrint";
import Reports from "@/pages/Reports";
import Automation from "@/pages/Automation";
import EmailCenter from "@/pages/EmailCenter";
import TriggerBuilder from "@/pages/TriggerBuilder";
import Integrations from "@/pages/Integrations";
import Jobs from "@/pages/Jobs";
import JobDetail from "@/pages/JobDetail";
import Fabrication from "@/pages/Fabrication";
import Blueprints from "@/pages/Blueprints";
import FabricationCalculatorPage from "@/pages/FabricationCalculatorPage";
import Orders from "@/pages/Orders";
import ProfitTracker from "@/pages/ProfitTracker";
import AuditLog from "@/pages/AuditLog";
import Clients from "@/pages/Clients";
import ClientDetail from "@/pages/ClientDetail";
import NewEnquiry from "@/pages/NewEnquiry";
import DailyTasksPage from "@/pages/DailyTasksPage";
import ClientPortalQuote from "@/pages/ClientPortalQuote";
import TeamAccess from "@/pages/TeamAccess";
import Scheduler from "@/pages/Scheduler";
import Approvals from "@/pages/Approvals";
import PublicEnquiry from "@/pages/PublicEnquiry";
import ClientPortalHome from "@/pages/ClientPortalHome";
import MeasureUpPack from "@/pages/MeasureUpPack";
import Stock from "@/pages/Stock";
import ManagementRocks from "@/pages/ManagementRocks";
import ManagementScorecard from "@/pages/ManagementScorecard";
import ManagementIssues from "@/pages/ManagementIssues";
import ManagementRisks from "@/pages/ManagementRisks";
import ManagementMeetings from "@/pages/ManagementMeetings";
import ManagementVision from "@/pages/ManagementVision";
import ManagementGoals from "@/pages/ManagementGoals";
import ManagementChart from "@/pages/ManagementChart";
import SupplierOnboarding from "@/pages/SupplierOnboarding";

function Protected({ children }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AdminGuard({ children }) {
  const { user } = useAuth();
  if (user?.role === "super_admin" || user?.role === "admin") return children;
  return <Navigate to={defaultRoute(user)} replace />;
}

function Guard({ k, children }) {
  const { user } = useAuth();
  const loc = useLocation();
  if (user?.role === "super_admin" || !k) return children;
  if ((user?.permissions || []).includes(k)) return children;
  const target = defaultRoute(user);
  // Never redirect to the page we're already blocked on (zero-permission
  // accounts) — show a friendly notice instead of looping.
  if (target === loc.pathname) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center px-6 text-center" data-testid="no-access-notice">
        <div className="font-display text-xl font-semibold text-slate-900">No pages enabled for your account</div>
        <p className="text-sm text-slate-500 mt-2 max-w-sm">Ask an admin to enable page access for you in Settings → Staff &amp; Roles, then sign in again.</p>
      </div>
    );
  }
  return <Navigate to={target} replace />;
}

function HomeRedirect() {
  const { user } = useAuth();
  return <Navigate to={defaultRoute(user)} replace />;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Toaster position="top-right" richColors closeButton />
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/setup" element={<Setup />} />
            <Route path="/portal/quote/:id" element={<ClientPortalQuote />} />
            <Route path="/portal/client/:cid" element={<ClientPortalHome />} />
            <Route path="/enquire" element={<PublicEnquiry />} />
            <Route path="/supplier-onboarding/:token" element={<SupplierOnboarding />} />
            <Route path="/quotes/:id/print" element={<Protected><QuotePrint /></Protected>} />
            <Route element={<Protected><AppLayout /></Protected>}>
              <Route path="/" element={<HomeRedirect />} />
              <Route path="/dashboard" element={<MyDashboard />} />
              <Route path="/tasks" element={<Guard k="tasks"><DailyTasksPage /></Guard>} />
              <Route path="/enquiry/new" element={<Guard k="enquiry"><NewEnquiry /></Guard>} />
              <Route path="/clients" element={<Guard k="clients"><Clients /></Guard>} />
              <Route path="/clients/:id" element={<Guard k="clients"><ClientDetail /></Guard>} />
              <Route path="/companies" element={<Guard k="companies"><Companies /></Guard>} />
              <Route path="/contacts" element={<Guard k="contacts"><Contacts /></Guard>} />
              <Route path="/suppliers" element={<Guard k="suppliers"><Suppliers /></Guard>} />
              <Route path="/materials" element={<Guard k="materials"><Materials /></Guard>} />
              <Route path="/material-orders" element={<Guard k="materials"><MaterialOrders /></Guard>} />
              <Route path="/deals" element={<Guard k="deals"><Deals /></Guard>} />
              <Route path="/activities" element={<Guard k="activities"><Activities /></Guard>} />
              <Route path="/quotes" element={<Guard k="quotes"><Quotes /></Guard>} />
              <Route path="/approvals" element={<Approvals />} />
              <Route path="/quotes/new" element={<Guard k="quotes"><QuoteBuilderV2 /></Guard>} />
              <Route path="/quotes/v2/:id" element={<Guard k="quotes"><QuoteBuilderV2 /></Guard>} />
              <Route path="/quotes/legacy/:id" element={<Guard k="quotes"><QuoteBuilder /></Guard>} />
              <Route path="/quotes/:id" element={<Guard k="quotes"><QuoteBuilderV2 /></Guard>} />
              {/* /pricing retired 2026-02 — consolidated into /materials. Redirect keeps old bookmarks working. */}
              <Route path="/pricing" element={<Navigate to="/materials" replace />} />
              <Route path="/scheduler" element={<Guard k="scheduler"><Scheduler /></Guard>} />
              <Route path="/jobs" element={<Guard k="calendar"><Jobs /></Guard>} />
              <Route path="/jobs/:id" element={<Guard k="calendar"><JobDetail /></Guard>} />
              <Route path="/fabrication" element={<Guard k="fabrication"><Fabrication /></Guard>} />
              <Route path="/fabrication/calculator" element={<Guard k="fab_calculator"><FabricationCalculatorPage /></Guard>} />
              <Route path="/blueprints" element={<Guard k="fabrication"><Blueprints /></Guard>} />
              <Route path="/measure-up" element={<Guard k="measure_up"><MeasureUpPack /></Guard>} />
              <Route path="/orders" element={<Guard k="orders"><Orders /></Guard>} />
              <Route path="/stock" element={<Guard k="stock"><Stock /></Guard>} />
              <Route path="/management/rocks" element={<Guard k="management"><ManagementRocks /></Guard>} />
              <Route path="/management/scorecard" element={<Guard k="management"><ManagementScorecard /></Guard>} />
              <Route path="/management/issues" element={<Guard k="management"><ManagementIssues /></Guard>} />
              <Route path="/management/risks" element={<Guard k="management"><ManagementRisks /></Guard>} />
              <Route path="/management/meetings" element={<Guard k="management"><ManagementMeetings /></Guard>} />
              <Route path="/management/vision" element={<Guard k="management"><ManagementVision /></Guard>} />
              <Route path="/management/goals" element={<Guard k="management"><ManagementGoals /></Guard>} />
              <Route path="/management/chart" element={<Guard k="management"><ManagementChart /></Guard>} />
              <Route path="/reports" element={<Guard k="reports"><Reports /></Guard>} />
              <Route path="/my-dashboard" element={<Navigate to="/dashboard" replace />} />
              <Route path="/whats-new" element={<WhatsNew />} />
              <Route path="/profit" element={<Guard k="profit"><ProfitTracker /></Guard>} />
              <Route path="/automation" element={<Guard k="automation"><Automation /></Guard>} />
              <Route path="/email" element={<Guard k="email"><EmailCenter /></Guard>} />
              <Route path="/email-log" element={<Navigate to="/email?tab=activity" replace />} />
              <Route path="/email-automations" element={<Navigate to="/email?tab=automations" replace />} />
              <Route path="/trigger-builder" element={<TriggerBuilder />} />
              <Route path="/integrations" element={<Guard k="integrations"><Integrations /></Guard>} />
              <Route path="/audit" element={<Guard k="audit"><AuditLog /></Guard>} />
              <Route path="/team" element={<Guard k="team"><TeamAccess /></Guard>} />
              <Route path="/backups" element={<AdminGuard><Backups /></AdminGuard>} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
